import { Injectable } from '@angular/core';
import {
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpInterceptor,
  HttpErrorResponse,
  HttpClient,
} from '@angular/common/http';
import { Observable, of, throwError } from 'rxjs';
import { SessionService } from '../services/session-service';
import { catchError, switchMap } from 'rxjs/operators';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ActivatedRoute, Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { environment } from 'src/environments/environment';

@Injectable()
export class TokenInterceptor implements HttpInterceptor {
  constructor(
    private sessionService: SessionService,
    private snackbar: MatSnackBar,
    private router: Router,
    private route: ActivatedRoute,
    private dialog: MatDialog,
    private http: HttpClient
  ) {}

  intercept(
    request: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    // Only the PlateX API gets the user's token (and the 401 refresh logic).
    // Every other host, including the SPA's own origin, is passed through
    // untouched. On dev2 the SPA origin sits behind Amplify Basic auth: a
    // Bearer header there replaces the browser's Basic credentials, Amplify
    // answers 401 and Chrome drops the cached login.
    if (!TokenInterceptor.isApiUrl(request.url)) {
      return next.handle(request);
    }

    // Expired token: refresh first (shared across requests) instead of sending
    // a request we know will 401 and then retrying it.
    if (
      !request.url.includes('/authenticate/refresh') &&
      this.sessionService.tokenExpiresSoon()
    ) {
      const original = request;
      return this.sessionService.RefreshToken().pipe(
        catchError((refreshError: HttpErrorResponse) => {
          // Refresh token gone: carry on as a guest, as the 401 path does.
          this.sessionService.invalidateSession();
          return of(null);
        }),
        switchMap(() => this.send(original, next))
      );
    }
    return this.send(request, next);
  }

  private send(
    request: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    request = this.AuthRequest(request);
    return next.handle(request).pipe(
      catchError((error: HttpErrorResponse) => {
        if (error.status == 401) {
          // If the refresh endpoint itself returned 401, the refresh token is
          // invalid or expired. Clear the session and let the app continue as
          // a guest — do not attempt another refresh or the loop is infinite.
          if (request.url.includes('/authenticate/refresh')) {
            this.sessionService.invalidateSession();
            return throwError(error);
          }

          if (this.sessionService.isLoggedIn()) {
            return this.sessionService.RefreshToken().pipe(
              // Only a failed REFRESH ends the session. The catch sits on the
              // refresh alone: if the retried request then fails (an admin-only
              // endpoint, a 500…), that error goes to the caller instead of
              // silently logging the user out.
              catchError((refreshError: HttpErrorResponse) => {
                this.sessionService.invalidateSession();
                return throwError(refreshError);
              }),
              switchMap(() => {
                request = this.AuthRequest(request);
                return next.handle(request);
              })
            );
          } else {
            return next.handle(request);
          }
        } else if (error.status == 404) {
          // this could be anything
          return next.handle(request);
        }

        return throwError(error);
      })
    );
  }

  /**
   * Exact hostnames of the PlateX API, taken from the same environment
   * fields UrlInterceptor resolves [HTTP-API-*] / [HTTP-DATA-API-*] to.
   */
  static apiHosts(): Set<string> {
    const addresses = [environment.apiBaseAddress, environment.dataApiBaseAddress];
    if (!environment.production) addresses.push(environment.local_apiBaseAddress);
    const hosts = new Set<string>();
    for (const address of addresses) {
      if (!address) continue;
      try {
        hosts.add(new URL(`https://${address}`).hostname);
      } catch {}
    }
    return hosts;
  }

  /** True only for absolute https URLs whose hostname is a PlateX API host. */
  static isApiUrl(url: string): boolean {
    let parsed: URL;
    try {
      parsed = new URL(url);
    } catch {
      return false; // relative URL: the SPA's own origin
    }
    return parsed.protocol === 'https:' && TokenInterceptor.apiHosts().has(parsed.hostname);
  }

  private AuthRequest(request: HttpRequest<any>): HttpRequest<any> {
    if (this.sessionService.isLoggedIn()) {
      request = request.clone({
        setHeaders: {
          Authorization: `Bearer ${this.sessionService.getToken()}`,
        },
      });
    } else {
      request = request.clone({
        setHeaders: {
          'Session-ID': localStorage.getItem('local_session'),
        },
      });
    }
    return request;
  }

  private HandleUnauth(): void {
    this.snackbar
      .open(
        'You need to be logged in to complete this action. Please login and try again',
        'Close',
        {
          duration: 3000,
        }
      )
      .afterDismissed()
      .subscribe(() => {
        this.sessionService.setSuccessRoute(this.router.url);
        this.router.navigate(['/login']);
      });
  }
}
